import { evaluateBSpline } from "./bspline";
import { bladeMesh, revolutionMesh } from "./bladeMesh";
import { getChartColors } from "../chartTheme";
import { buildTheme } from "../themes";
import useAuthStore from "../store/authStore";
import {
  Document, Packer, Paragraph, TextRun, ImageRun,
  Table, TableRow, TableCell,
  AlignmentType, WidthType, BorderStyle, ShadingType, PageBreak,
} from "docx";

// Convert "R G B" theme color string → "#rrggbb" (for Plotly / CSS)
const _hex = (rgb) =>
  "#" + rgb.split(" ").map(n => parseInt(n).toString(16).padStart(2, "0")).join("");
// Convert "R G B" theme color string → "RRGGBB" (for docx, no #)
const _dhex = (rgb) =>
  rgb.split(" ").map(n => parseInt(n).toString(16).padStart(2, "0")).join("").toUpperCase();

function _cssRgb(shade) {
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(`--bh-${shade}`)
      .trim() || "59 130 246"
  );
}

// DOCX palette — read at module load time (static; updated at next page load)
const D_HDR_FILL  = _dhex(_cssRgb(900));
const D_H1        = _dhex(_cssRgb(900));
const D_H2        = _dhex(_cssRgb(700));
const D_LABEL     = _dhex(_cssRgb(800));
const D_BORDER    = _dhex(_cssRgb(200));
const D_ROW_ALT   = _dhex(_cssRgb(50));

// Returns live Plotly colors from current CSS vars (called at export time).
function _getFigColors() {
  const { COLOR_DARK, COLOR_LIGHT, COLOR_PRIMARY } = getChartColors();
  const SURF_COLORS = [900, 700, 500, 300, 100].map(s => _hex(_cssRgb(s)));
  const surfColor = (name, i = 0) => {
    const MAP = { HUB: 0, "SL-1": 1, "SL-2": 2, "SL-3": 3, SHROUD: 4 };
    return SURF_COLORS[MAP[name] ?? (i % SURF_COLORS.length)];
  };
  return { COLOR_DARK, COLOR_LIGHT, COLOR_PRIMARY, SURF_COLORS, surfColor };
}

const BASE_LAYOUT = {
  paper_bgcolor: "white",
  plot_bgcolor:  "#F9FAFA",
  font: { family: "Inter, Arial, sans-serif", size: 12, color: "#333" },
  legend: { bgcolor: "rgba(255,255,255,0.85)", bordercolor: "#E0E0E0", borderwidth: 1,
            x: 0, xanchor: "left", y: 1, yanchor: "top", font: { size: 10 } },
};

// ── Figure spec builders (mirrors View2DPage charts) ──────────────────────

export function mecoFigure(views) {
  const { SURF_COLORS, surfColor } = _getFigColors();
  const raw       = views.hub_shroud     || [];
  const processed = views.processed_lines || [];
  const hasProc   = processed.length > 0;

  const colorByName = {};
  raw.forEach((l, i) => { colorByName[l.name] = surfColor(l.name, i); });

  const traces = [
    // Raw MECO polylines — faded/dotted when processed lines exist
    ...raw.map((l, i) => ({
      type: "scatter", mode: "lines",
      name: hasProc ? `${l.name} (MECO)` : l.name,
      x: l.r, y: l.z,
      showlegend: !hasProc,
      line: {
        color: hasProc ? surfColor(l.name, i) + "40" : surfColor(l.name, i),
        width: hasProc ? 1 : 1.8,
        dash:  hasProc ? "dot" : "solid",
      },
    })),
    // Processed / trimmed skeleton lines
    ...processed.map(l => ({
      type: "scatter", mode: "lines", name: l.name,
      x: l.r, y: l.z,
      line: { color: colorByName[l.name] || SURF_COLORS[0], width: 2 },
    })),
    // LE / TE edge connectors + markers
    ...(hasProc ? [
      { type: "scatter", mode: "lines", name: "LE edge", showlegend: false,
        x: processed.map(l => l.le_r), y: processed.map(l => l.le_z),
        line: { color: "#111827", width: 1 }, hoverinfo: "skip" },
      { type: "scatter", mode: "lines", name: "TE edge", showlegend: false,
        x: processed.map(l => l.te_r), y: processed.map(l => l.te_z),
        line: { color: "#111827", width: 1 }, hoverinfo: "skip" },
      { type: "scatter", mode: "markers", name: "LE",
        x: processed.map(l => l.le_r), y: processed.map(l => l.le_z),
        marker: { symbol: "circle", size: 8, color: "white",
                  line: { color: processed.map(l => colorByName[l.name] || "#333"), width: 2 } } },
      { type: "scatter", mode: "markers", name: "TE",
        x: processed.map(l => l.te_r), y: processed.map(l => l.te_z),
        marker: { symbol: "square", size: 8, color: "white",
                  line: { color: processed.map(l => colorByName[l.name] || "#333"), width: 2 } } },
    ] : []),
    // Cut-off line
    ...(views.cutoff_line?.r?.length ? [{
      type: "scatter", mode: "lines+markers", name: "Cut-off",
      x: views.cutoff_line.r, y: views.cutoff_line.z,
      line: { color: "#111827", width: 2, dash: "dash" },
      marker: { color: "#111827", size: 7 },
    }] : []),
  ];

  return {
    data: traces,
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Hub – Shroud", font: { size: 15, color: _hex(_cssRgb(900)) } },
      margin: { l: 65, r: 20, t: 55, b: 65 },
      xaxis: { title: "r  [mm]", scaleanchor: "y", scaleratio: 1, gridcolor: "#E4E4E4" },
      yaxis: { title: "z  [mm]", gridcolor: "#E4E4E4" },
    },
  };
}

export function confFigure(views) {
  const { surfColor } = _getFigColors();
  const traces = (views.conf_map || []).map((l, i) => ({
    type: "scatter", mode: "lines", name: l.name,
    x: l.m, y: l.s,
    line: { color: surfColor(l.name, i), width: 1.8, shape: "spline" },
  }));
  return {
    data: traces,
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Conformal Mapping", font: { size: 14, color: _hex(_cssRgb(900)) } },
      margin: { l: 75, r: 20, t: 50, b: 65 },
      xaxis: { title: "m  [mm]", scaleanchor: "y", scaleratio: 1, gridcolor: "#E4E4E4" },
      yaxis: { title: "∫r dθ  [mm]", gridcolor: "#E4E4E4" },
    },
  };
}

export function wrapTopFigure(views) {
  const { surfColor } = _getFigColors();
  const DEG2RAD = Math.PI / 180;
  const colorByName = {};
  (views.hub_shroud || []).forEach((l, i) => { colorByName[l.name] = surfColor(l.name, i); });

  const traces = [
    ...(views.wrap || []).map((l, i) => ({
      type: "scatter", mode: "lines", name: l.name,
      x: l.theta_deg.map((t, j) => l.r[j] * Math.cos(t * DEG2RAD)),
      y: l.theta_deg.map((t, j) => l.r[j] * Math.sin(t * DEG2RAD)),
      line: { color: surfColor(l.name, i), width: 1.8, shape: "spline" },
    })),
    ...(views.thickness_lines || []).flatMap((l, i) => {
      const color = colorByName[l.name] || surfColor(l.name, i);
      return [
        { type: "scatter", mode: "lines", name: `${l.name} PS`, showlegend: false,
          x: l.ps_x, y: l.ps_y, line: { color, width: 0.8, shape: "spline" } },
        { type: "scatter", mode: "lines", name: `${l.name} SS`, showlegend: false,
          x: l.ss_x, y: l.ss_y, line: { color, width: 0.8, shape: "spline" } },
      ];
    }),
  ];

  return {
    data: traces,
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Wrap (Top View)", font: { size: 14, color: _hex(_cssRgb(900)) } },
      margin: { l: 65, r: 20, t: 50, b: 65 },
      xaxis: { title: "x  [mm]", scaleanchor: "y", scaleratio: 1, gridcolor: "#E4E4E4" },
      yaxis: { title: "y  [mm]", gridcolor: "#E4E4E4" },
    },
  };
}

export function coneAreaFigure(views) {
  const ca = views.cone_area;
  if (!ca) return null;
  return {
    data: [{
      type: "scatter", mode: "lines", name: "Hub",
      x: ca.m, y: ca.area,
      line: { color: "#111827", width: 1.5, shape: "spline" },
    }],
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Meridional Cone Area Development", font: { size: 14, color: _hex(_cssRgb(900)) } },
      margin: { l: 80, r: 30, t: 50, b: 65 },
      xaxis: { title: "m  [mm]", gridcolor: "#E4E4E4" },
      yaxis: { title: "Cone area  [mm²]", gridcolor: "#E4E4E4" },
    },
  };
}

export function conePairsFigure(views) {
  const { SURF_COLORS } = _getFigColors();
  const ca = views.cone_area;
  if (!ca?.hub_r || !ca?.shr_r) return null;

  const r_segs = [], z_segs = [];
  for (let j = 0; j < ca.hub_r.length; j++) {
    r_segs.push(ca.hub_r[j], ca.shr_r[j], null);
    z_segs.push(ca.hub_z[j], ca.shr_z[j], null);
  }

  return {
    data: [
      { type: "scatter", mode: "lines", name: "Hub",
        x: ca.hub_r, y: ca.hub_z, line: { color: SURF_COLORS[0], width: 2 } },
      { type: "scatter", mode: "lines", name: "Shroud",
        x: ca.shr_r, y: ca.shr_z, line: { color: SURF_COLORS[4], width: 2 } },
      { type: "scatter", mode: "lines", name: "Cone pairs", showlegend: false,
        x: r_segs, y: z_segs, line: { color: "#9ca3af", width: 0.8 } },
    ],
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Cone Pairs", font: { size: 14, color: _hex(_cssRgb(900)) } },
      margin: { l: 65, r: 20, t: 50, b: 65 },
      xaxis: { title: "r  [mm]", scaleanchor: "y", scaleratio: 1, gridcolor: "#E4E4E4" },
      yaxis: { title: "z  [mm]", gridcolor: "#E4E4E4" },
    },
  };
}

// Porcupine curvature — mirrors View2DPage curvatureTraces() exactly.
// κ = r'z'' − z'r''  (arc-length parameterisation), smoothed with window W=12.
// Normal vectors drawn from curve points scaled by normalised κ ∈ [-1,1].
export function curvatureFigure(views) {
  const { SURF_COLORS, surfColor } = _getFigColors();
  const processed = views.processed_lines || [];
  const colorByName = {};
  (views.hub_shroud || []).forEach((l, i) => { colorByName[l.name] = surfColor(l.name, i); });

  const hubLine    = processed.find(l => l.name.toUpperCase().includes("HUB"));
  const shroudLine = processed.find(l => l.name.toUpperCase().includes("SHROUD"));
  const targets    = [hubLine, shroudLine].filter(Boolean);
  if (targets.length === 0) return null;

  const CURV_SCALE = 30;

  function curvOf(r, z) {
    const n = r.length;
    if (n < 4) return null;
    let totalS = 0;
    for (let i = 1; i < n; i++) {
      const dr = r[i] - r[i - 1], dz = z[i] - z[i - 1];
      totalS += Math.sqrt(dr * dr + dz * dz);
    }
    const h = totalS / (n - 1);
    const kappa = new Float64Array(n);
    const nr    = new Float64Array(n);
    const nz    = new Float64Array(n);
    for (let i = 1; i < n - 1; i++) {
      const drds  = (r[i + 1] - r[i - 1]) / (2 * h);
      const dzds  = (z[i + 1] - z[i - 1]) / (2 * h);
      const d2rds = (r[i + 1] - 2 * r[i] + r[i - 1]) / (h * h);
      const d2zds = (z[i + 1] - 2 * z[i] + z[i - 1]) / (h * h);
      kappa[i] = drds * d2zds - dzds * d2rds;
      nr[i]    = -dzds;
      nz[i]    =  drds;
    }
    kappa[0] = kappa[1];     nr[0] = nr[1];     nz[0] = nz[1];
    kappa[n - 1] = kappa[n - 2]; nr[n - 1] = nr[n - 2]; nz[n - 1] = nz[n - 2];
    const W = 12;
    const smoothed = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      let sum = 0, cnt = 0;
      for (let j = Math.max(0, i - W); j <= Math.min(n - 1, i + W); j++) { sum += kappa[j]; cnt++; }
      smoothed[i] = sum / cnt;
    }
    return { kappa: smoothed, nr, nz };
  }

  const curves = targets.map(l => ({ line: l, c: curvOf(l.r, l.z) })).filter(x => x.c);
  const maxK = Math.max(...curves.flatMap(({ c }) => Array.from(c.kappa).map(Math.abs)));
  if (maxK < 1e-12) return null;

  const porcupineTraces = curves.flatMap(({ line, c }) => {
    const { r, z } = line;
    const rSegs = [], zSegs = [];
    for (let i = 0; i < r.length; i++) {
      const kn = c.kappa[i] / maxK;
      rSegs.push(r[i], r[i] + CURV_SCALE * kn * c.nr[i], null);
      zSegs.push(z[i], z[i] + CURV_SCALE * kn * c.nz[i], null);
    }
    return [{ type: "scatter", mode: "lines", name: `${line.name} κ`,
              x: rSegs, y: zSegs,
              line: { color: colorByName[line.name] || SURF_COLORS[0], width: 0.8 },
              hoverinfo: "skip" }];
  });

  const baseTraces = targets.map(l => ({
    type: "scatter", mode: "lines", name: l.name,
    x: l.r, y: l.z,
    line: { color: colorByName[l.name] || SURF_COLORS[0], width: 2 },
  }));

  return {
    data: [...baseTraces, ...porcupineTraces],
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Curvature (Porcupine)", font: { size: 14, color: _hex(_cssRgb(900)) } },
      margin: { l: 65, r: 20, t: 50, b: 65 },
      xaxis: { title: "r  [mm]", scaleanchor: "y", scaleratio: 1, gridcolor: "#E4E4E4" },
      yaxis: { title: "z  [mm]", gridcolor: "#E4E4E4" },
    },
  };
}

export function betaFigure(constructionLines) {
  const { surfColor } = _getFigColors();
  const traces = [];
  (constructionLines || []).forEach((cl, i) => {
    if (!cl.active || !cl.beta_spline?.control_points?.length) return;
    const { control_points: cps, degree } = cl.beta_spline;
    const pts = evaluateBSpline(cps, degree ?? 3, 300);
    const c = surfColor(cl.name, i);
    traces.push({ x: pts.map((p) => p[0]), y: pts.map((p) => p[1]),
                  mode: "lines", name: cl.name, line: { color: c, width: 2.5 } });
    traces.push({ x: cps.map((p) => p[0]), y: cps.map((p) => p[1]),
                  mode: "markers", showlegend: false,
                  marker: { color: c, size: 7, opacity: 0.75 } });
  });
  return {
    data: traces,
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Blade Angle Distribution", font: { size: 15, color: _hex(_cssRgb(900)) } },
      margin: { l: 70, r: 30, t: 55, b: 70 },
      xaxis: { title: "Meridional coordinate  m  [–]", range: [0, 1], gridcolor: "#E4E4E4" },
      yaxis: { title: "Blade angle  β  [°]", gridcolor: "#E4E4E4" },
    },
  };
}

export function thicknessFigure(constructionLines) {
  const { surfColor } = _getFigColors();
  const traces = [];
  (constructionLines || []).forEach((cl, i) => {
    if (!cl.active || !cl.thickness_spline?.control_points?.length) return;
    const ts  = cl.thickness_spline;
    const mul = (ts.multipliers ?? {})[cl.name] ?? 1.0;
    const pts = evaluateBSpline(ts.control_points, ts.degree ?? 3, 300);
    const c   = surfColor(cl.name, i);
    traces.push({ x: pts.map((p) => p[0]), y: pts.map((p) => p[1] * mul),
                  mode: "lines", name: cl.name, line: { color: c, width: 2.5 } });
    traces.push({ x: ts.control_points.map((p) => p[0]),
                  y: ts.control_points.map((p) => p[1] * mul),
                  mode: "markers", showlegend: false,
                  marker: { color: c, size: 7, opacity: 0.75 } });
  });
  return {
    data: traces,
    layout: {
      ...BASE_LAYOUT,
      title: { text: "Blade Thickness Distribution", font: { size: 15, color: _hex(_cssRgb(900)) } },
      margin: { l: 70, r: 30, t: 55, b: 70 },
      xaxis: { title: "Arc-length coordinate  s  [–]", range: [0, 1], gridcolor: "#E4E4E4" },
      yaxis: { title: "Thickness  t  [mm]", gridcolor: "#E4E4E4" },
    },
  };
}

export function view3dFigure(views, nBlades = 6) {
  const { COLOR_DARK, COLOR_LIGHT } = _getFigColors();
  const { blade, hub, shroud } = views.surface_3d || {};
  if (!blade) return null;
  const traces = [];
  if (blade)  traces.push(bladeMesh(blade, nBlades, COLOR_DARK, "Blade", 0.90));
  if (hub)    traces.push(revolutionMesh(hub.x, hub.z, COLOR_DARK, "Hub", 0.35));
  if (shroud) traces.push(revolutionMesh(shroud.x, shroud.z, COLOR_LIGHT, "Shroud", 0.18));
  if (views.cutoff_line)
    traces.push(revolutionMesh(views.cutoff_line.r, views.cutoff_line.z, "#374151", "Cut-off", 0.15));
  return {
    data: traces,
    layout: {
      margin: { l: 0, r: 0, t: 30, b: 0 },
      paper_bgcolor: "white", plot_bgcolor: "#F9FAFA",
      showlegend: true,
      legend: { font: { size: 11 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
      scene: {
        xaxis: { title: "x [mm]", gridcolor: "#e5e7eb" },
        yaxis: { title: "y [mm]", gridcolor: "#e5e7eb" },
        zaxis: { title: "z [mm]", gridcolor: "#e5e7eb" },
        aspectmode: "data",
        camera: { eye: { x: 1.5, y: 1.5, z: 0.8 } },
      },
    },
  };
}

function _inflectionPoints(curvData, hasSurface) {
  const { kappa_v, u, v, surface_x, surface_y, surface_z } = curvData;
  const nv = kappa_v.length, nu = kappa_v[0]?.length ?? 0;
  const cx = [], cy = [], cz = [], sx = [], sy = [], sz = [];
  for (let iv = 0; iv < nv; iv++) {
    for (let iu = 0; iu < nu - 1; iu++) {
      const k0 = kappa_v[iv][iu], k1 = kappa_v[iv][iu + 1];
      if (k0 * k1 < 0) {
        const t = k0 / (k0 - k1);
        cx.push(u[iu] + t * (u[iu + 1] - u[iu])); cy.push(v[iv]); cz.push(0);
        if (hasSurface) {
          sx.push(surface_x[iv][iu] + t * (surface_x[iv][iu + 1] - surface_x[iv][iu]));
          sy.push(surface_y[iv][iu] + t * (surface_y[iv][iu + 1] - surface_y[iv][iu]));
          sz.push(surface_z[iv][iu] + t * (surface_z[iv][iu + 1] - surface_z[iv][iu]));
        }
      }
    }
  }
  for (let iv = 0; iv < nv - 1; iv++) {
    for (let iu = 0; iu < nu; iu++) {
      const k0 = kappa_v[iv][iu], k1 = kappa_v[iv + 1][iu];
      if (k0 * k1 < 0) {
        const t = k0 / (k0 - k1);
        cx.push(u[iu]); cy.push(v[iv] + t * (v[iv + 1] - v[iv])); cz.push(0);
        if (hasSurface) {
          sx.push(surface_x[iv][iu] + t * (surface_x[iv + 1][iu] - surface_x[iv][iu]));
          sy.push(surface_y[iv][iu] + t * (surface_y[iv + 1][iu] - surface_y[iv][iu]));
          sz.push(surface_z[iv][iu] + t * (surface_z[iv + 1][iu] - surface_z[iv][iu]));
        }
      }
    }
  }
  return { cx, cy, cz, sx, sy, sz };
}

export function curvParamFigure(curvData) {
  const { COLOR_DARK, COLOR_PRIMARY } = _getFigColors();
  if (!curvData?.kappa_v?.length) return null;
  const flat   = curvData.kappa_v.flat(1);
  const maxAbs = flat.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
  const zRange = maxAbs < 1e-12 ? [-1e-4, 1e-4] : [-maxAbs, maxAbs];
  const { cx, cy, cz } = _inflectionPoints(curvData, false);
  const traces = [
    {
      type: "surface", x: curvData.u, y: curvData.v, z: curvData.kappa_v,
      surfacecolor: curvData.kappa_v,
      colorscale: [[0, COLOR_DARK], [0.5, "#ffffff"], [1, COLOR_PRIMARY]],
      cmin: zRange[0], cmax: zRange[1],
      colorbar: { title: { text: "κᵥ [mm⁻¹]", side: "right", font: { size: 11 } }, thickness: 14, len: 0.6, tickfont: { size: 10 } },
      opacity: 0.92,
      lighting: { ambient: 0.8, diffuse: 0.6, specular: 0.1 },
      showlegend: false,
    },
    ...(cx.length ? [{ type: "scatter3d", x: cx, y: cy, z: cz, mode: "markers",
                        marker: { size: 2.5, color: "#111827", opacity: 0.85 }, hoverinfo: "skip", showlegend: false }] : []),
  ];
  return {
    data: traces,
    layout: {
      margin: { l: 0, r: 0, t: 30, b: 0 }, paper_bgcolor: "white",
      scene: {
        xaxis: { title: { text: "u (streamwise)", font: { size: 10 } }, gridcolor: "#e5e7eb" },
        yaxis: { title: { text: "v (spanwise)",   font: { size: 10 } }, gridcolor: "#e5e7eb" },
        zaxis: { title: { text: "κᵥ [mm⁻¹]", font: { size: 10 } }, gridcolor: "#e5e7eb", range: zRange },
        aspectmode: "manual", aspectratio: { x: 2, y: 1, z: 0.8 },
        camera: { eye: { x: 0, y: -2.0, z: 1.2 } },
      },
      showlegend: false,
    },
  };
}

export function curvSkeletonFigure(curvData) {
  const { COLOR_DARK, COLOR_LIGHT } = _getFigColors();
  if (!curvData?.surface_x?.length) return null;
  const { sx, sy, sz } = _inflectionPoints(curvData, true);
  const traces = [
    {
      type: "surface",
      x: curvData.surface_x, y: curvData.surface_y, z: curvData.surface_z,
      surfacecolor: curvData.surface_z,
      colorscale: [[0, COLOR_LIGHT], [1, COLOR_DARK]],
      showscale: false, opacity: 0.88,
      lighting: { ambient: 0.75, diffuse: 0.8, specular: 0.25 },
      showlegend: false,
    },
    ...(sx.length ? [{ type: "scatter3d", x: sx, y: sy, z: sz, mode: "markers",
                        marker: { size: 2.5, color: "#111827", opacity: 0.85 }, hoverinfo: "skip", showlegend: false }] : []),
  ];
  return {
    data: traces,
    layout: {
      margin: { l: 0, r: 0, t: 30, b: 0 }, paper_bgcolor: "white",
      scene: {
        xaxis: { title: { text: "x [mm]", font: { size: 10 } }, gridcolor: "#e5e7eb" },
        yaxis: { title: { text: "y [mm]", font: { size: 10 } }, gridcolor: "#e5e7eb" },
        zaxis: { title: { text: "z [mm]", font: { size: 10 } }, gridcolor: "#e5e7eb" },
        aspectmode: "data",
        camera: { eye: { x: 1.5, y: 1.5, z: 0.8 } },
      },
      showlegend: false,
    },
  };
}

// ── DOCX helpers ───────────────────────────────────────────────────────────

function dataUrlToBytes(dataUrl) {
  const b64 = dataUrl.split(",")[1];
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const NONE_BORDER = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
const THIN_BORDER = { style: BorderStyle.SINGLE, size: 1, color: D_BORDER };
const TABLE_BORDERS = {
  top: NONE_BORDER, bottom: NONE_BORDER,
  left: NONE_BORDER, right: NONE_BORDER,
  insideH: NONE_BORDER, insideV: NONE_BORDER,
};
const CELL_BORDERS = {
  top: NONE_BORDER, bottom: THIN_BORDER,
  left: NONE_BORDER, right: NONE_BORDER,
};

const FONT = "Arial";

function metaRow(label, value) {
  return new TableRow({
    children: [
      new TableCell({
        width: { size: 38, type: WidthType.PERCENTAGE },
        borders: CELL_BORDERS,
        children: [new Paragraph({
          spacing: { before: 60, after: 60 },
          children: [new TextRun({ text: String(label), bold: true, size: 18, color: D_LABEL, font: FONT })],
        })],
      }),
      new TableCell({
        width: { size: 62, type: WidthType.PERCENTAGE },
        borders: CELL_BORDERS,
        children: [new Paragraph({
          spacing: { before: 60, after: 60 },
          children: [new TextRun({ text: String(value ?? "—"), size: 18, color: "1A1A1A", font: FONT })],
        })],
      }),
    ],
  });
}

function metaTable(rows) {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: TABLE_BORDERS,
    rows: rows.map(([label, value]) => metaRow(label, value)),
  });
}

function sectionHeading(text) {
  return new Paragraph({
    spacing: { before: 340, after: 140 },
    children: [new TextRun({ text, bold: true, size: 22, color: D_H1, font: FONT })],
  });
}

function chartImage(bytes, widthPx, heightPx) {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: 160, after: 80 },
    children: [new ImageRun({ data: bytes, transformation: { width: widthPx, height: heightPx }, type: "png" })],
  });
}

function captionPara(text) {
  return new Paragraph({
    spacing: { before: 40, after: 200 },
    children: [new TextRun({ text, italics: true, size: 18, color: "666666", font: FONT })],
  });
}

function pb() {
  return new Paragraph({ children: [new PageBreak()] });
}

function subHeading(text) {
  return new Paragraph({
    spacing: { before: 260, after: 100 },
    children: [new TextRun({ text, bold: true, size: 19, color: D_H2, font: FONT })],
  });
}

// Generic data table with a dark header row and alternating row shading
// alignments: optional array of AlignmentType values per column
// widthType: WidthType.PERCENTAGE (default) or WidthType.DXA for fixed inch-based widths
function dataTable(headers, rows, colWidths, alignments, widthType = WidthType.PERCENTAGE) {
  const hdrBorders = { top: NONE_BORDER, bottom: NONE_BORDER, left: NONE_BORDER, right: NONE_BORDER };
  const totalDxa = colWidths && widthType === WidthType.DXA
    ? colWidths.reduce((s, w) => s + w, 0) : null;

  const headerRow = new TableRow({
    tableHeader: true,
    children: headers.map((h, ci) =>
      new TableCell({
        width: colWidths ? { size: colWidths[ci], type: widthType } : undefined,
        shading: { fill: D_HDR_FILL, type: ShadingType.CLEAR },
        borders: hdrBorders,
        children: [new Paragraph({
          alignment: alignments?.[ci] ?? AlignmentType.LEFT,
          spacing: { before: 60, after: 60 },
          children: [new TextRun({ text: String(h), bold: true, size: 16, color: "FFFFFF", font: FONT })],
        })],
      })
    ),
  });

  const dataRows = rows.map((row, ri) =>
    new TableRow({
      children: row.map((cell, ci) =>
        new TableCell({
          width: colWidths ? { size: colWidths[ci], type: widthType } : undefined,
          shading: { fill: ri % 2 === 1 ? D_ROW_ALT : "FFFFFF", type: ShadingType.CLEAR },
          borders: CELL_BORDERS,
          children: [new Paragraph({
            alignment: alignments?.[ci] ?? AlignmentType.LEFT,
            spacing: { before: 55, after: 55 },
            children: [new TextRun({ text: String(cell ?? "—"), size: 16, color: "1A1A1A", font: FONT })],
          })],
        })
      ),
    })
  );

  return new Table({
    width: totalDxa
      ? { size: totalDxa, type: WidthType.DXA }
      : { size: 100, type: WidthType.PERCENTAGE },
    borders: TABLE_BORDERS,
    rows: [headerRow, ...dataRows],
  });
}

function fmt(v, dec = 3) {
  return v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(dec) : "—";
}

// Build all input-data table elements (to be spliced into the children array)
function constructionLineTables(blade, views) {
  const cls     = (blade.construction_lines ?? []).filter(cl => cl.active);
  const procMap = Object.fromEntries(
    (views.processed_lines ?? []).map(l => [l.name, l])
  );

  const out = [];

  // ── MECO Control Points ────────────────────────────────────────────────
  const clsWithMeco = cls.filter(cl => cl.r_coords?.length);
  if (clsWithMeco.length) {
    out.push(sectionHeading("MECO Construction Points"));
    for (const cl of clsWithMeco) {
      out.push(subHeading(cl.name));
      const rows = cl.r_coords.map((r, i) => [i + 1, fmt(r), fmt(cl.z_coords?.[i])]);
      out.push(dataTable(["#", "r  [mm]", "z  [mm]"], rows, [15, 42, 43]));
    }
  }

  // ── LE / TE Edge Coordinates ───────────────────────────────────────────
  const clsWithEdge = cls.filter(cl => cl.r_coords?.length >= 2);
  if (clsWithEdge.length) {
    out.push(sectionHeading("LE / TE Edge Coordinates"));

    const leRows = clsWithEdge.map(cl => {
      const proc = procMap[cl.name];
      return [
        cl.name,
        fmt(cl.le_r),
        fmt(cl.le_z),
        proc ? fmt(proc.le_r) : "—",
        proc ? fmt(proc.le_z) : "—",
      ];
    });
    out.push(subHeading("Leading Edge"));
    out.push(dataTable(
      ["Line", "Orig r  [mm]", "Orig z  [mm]", "Snapped r  [mm]", "Snapped z  [mm]"],
      leRows,
      [18, 20, 20, 21, 21],
    ));

    const teRows = clsWithEdge.map(cl => {
      const proc = procMap[cl.name];
      return [
        cl.name,
        fmt(cl.te_r),
        fmt(cl.te_z),
        proc ? fmt(proc.te_r) : "—",
        proc ? fmt(proc.te_z) : "—",
      ];
    });
    out.push(subHeading("Trailing Edge"));
    out.push(dataTable(
      ["Line", "Orig r  [mm]", "Orig z  [mm]", "Snapped r  [mm]", "Snapped z  [mm]"],
      teRows,
      [18, 20, 20, 21, 21],
    ));
  }

  // ── Beta Spline Control Points ─────────────────────────────────────────
  const clsWithBeta = cls.filter(cl => cl.beta_spline?.control_points?.length);
  if (clsWithBeta.length) {
    out.push(sectionHeading("Blade Angle Control Points (B-spline)"));
    const fixedEnd = blade.fix_end ? "TE" : "LE";
    for (const cl of clsWithBeta) {
      out.push(subHeading(cl.name));
      out.push(metaTable([
        ["Degree",     cl.beta_spline.degree ?? "—"],
        ["Fixed end",  fixedEnd],
        ["Start θ",    fmt(cl.theta_start ?? 0, 2) + " °"],
      ]));
      const rows = cl.beta_spline.control_points.map((pt, i) => [i + 1, fmt(pt[0], 4), fmt(pt[1], 2)]);
      out.push(dataTable(["#", "m  [–]", "β  [°]"], rows, [15, 42, 43]));
    }
  }

  // ── Thickness Spline Control Points ────────────────────────────────────
  const clsWithThick = cls.filter(cl => cl.thickness_spline?.control_points?.length);
  if (clsWithThick.length) {
    out.push(sectionHeading("Blade Thickness Control Points (B-spline)"));
    for (const cl of clsWithThick) {
      const mul = (cl.thickness_spline.multipliers ?? {})[cl.name] ?? 1.0;
      out.push(subHeading(cl.name + (mul !== 1.0 ? ` ×${fmt(mul, 3)}` : "")));
      out.push(metaTable([
        ["Degree", cl.thickness_spline.degree ?? "—"],
      ]));
      const rows = cl.thickness_spline.control_points.map((pt, i) => [
        i + 1, fmt(pt[0], 4), fmt(pt[1] * mul, 3),
      ]);
      out.push(dataTable(["#", "s  [–]", "t  [mm]"], rows,
        [720, 720, 720],
        [AlignmentType.LEFT, AlignmentType.LEFT, AlignmentType.LEFT],
        WidthType.DXA));
    }
  }

  return out;
}

// ── Main export ────────────────────────────────────────────────────────────

/**
 * Generate and download a DOCX report.
 *
 * Plotly is received as a parameter (window.Plotly from CDN) so this module
 * never imports plotly.js directly and avoids webpack-5 Node polyfill errors.
 */
async function fetchImageBytes(url) {
  const resp = await fetch(url);
  const buf  = await resp.arrayBuffer();
  return new Uint8Array(buf);
}

function measureImage(bytes) {
  return new Promise((resolve) => {
    const blob = new Blob([bytes]);
    const url  = URL.createObjectURL(blob);
    const img  = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve({ w: 0, h: 0 }); };
    img.src = url;
  });
}

function fitDimensions(naturalW, naturalH, maxW, maxH) {
  if (!naturalW || !naturalH) return { width: maxW, height: maxH };
  const scale = Math.min(maxW / naturalW, maxH / naturalH);
  return { width: Math.round(naturalW * scale), height: Math.round(naturalH * scale) };
}

export async function exportDesignDocx(blade, project, views, curvData, perf, beta1, Plotly) {
  const toImg = async (fig, w = 900, h = 640) =>
    dataUrlToBytes(await Plotly.toImage(fig, { format: "png", width: w, height: h }));

  const children = [];

  // ── Cover / metadata ────────────────────────────────────────────────────

  // Header logo from active entity theme
  const _activeTheme = buildTheme(useAuthStore.getState().user?.entity_theme);
  if (_activeTheme.headerLogo?.src) {
    try {
      const logoUrl   = window.location.origin + _activeTheme.headerLogo.src;
      const logoBytes = await fetchImageBytes(logoUrl);
      const nat       = await measureImage(logoBytes);
      const dims      = fitDimensions(nat.w, nat.h, 200, 60);
      children.push(new Paragraph({
        spacing: { after: 200 },
        children: [new ImageRun({ data: logoBytes, transformation: dims, type: "png" })],
      }));
    } catch (_) { /* logo unreachable — skip silently */ }
  }

  children.push(
    new Paragraph({
      spacing: { after: 100 },
      children: [new TextRun({ text: "Blade Design Report", bold: true, size: 52, color: D_H1, font: FONT })],
    }),
    new Paragraph({
      spacing: { after: 480 },
      children: [new TextRun({ text: blade.name, size: 28, color: D_H2, font: FONT })],
    }),
  );

  children.push(sectionHeading("Design Identity"));
  children.push(metaTable([
    ["Name",        blade.name],
    ["HYCO ID",     blade.hyco_id ?? "—"],
    ["Version",     blade.version ?? 1],
    ["Description", blade.description ?? "—"],
  ]));

  children.push(sectionHeading("Project"));
  children.push(metaTable([
    ["Name",         project?.name ?? "—"],
    ["Machine type", project?.machine_type?.label ?? "—"],
  ]));

  children.push(sectionHeading("Hydraulic Operating Point"));
  children.push(metaTable([
    ["Speed n",      blade.rpm       != null ? blade.rpm + " rpm"        : "—"],
    ["Flow rate Q",  blade.flow_rate != null ? blade.flow_rate + " m³/s" : "—"],
    ["Total head H", blade.head      != null ? blade.head + " m"         : "—"],
    ["No. blades",   blade.n_blades  ?? "—"],
  ]));

  children.push(sectionHeading("Geometry Settings"));
  children.push(metaTable([
    ["Topology",          blade.topology          ?? "—"],
    ["Thickness option",  blade.thickness_option  ?? "—"],
    ["Streamwise degree", blade.degree_streamwise ?? "—"],
    ["Spanwise degree",   blade.degree_spanwise   ?? "—"],
    ["Samples n",         blade.n_samples         ?? "—"],
    ["Density factor",    (blade.density_factor   ?? 0).toFixed(2)],
  ]));

  if (perf && !perf.error) {
    const perfRows = Object.entries(perf)
      .filter(([k]) => k !== "error")
      .map(([k, v]) => [k, typeof v === "number" ? v.toFixed(4) : String(v)]);
    if (perfRows.length) {
      children.push(sectionHeading("Performance Coefficients"));
      children.push(metaTable(perfRows));
    }
  }

  if (beta1 && !beta1.error && beta1.surfaces) {
    children.push(sectionHeading("Inlet Conditions"));
    children.push(metaTable([
      ["Inlet area A₁", (beta1.A_inlet_m2 ?? 0).toFixed(6) + " m²"],
      ["cₘ₁",           (beta1.cm1_m_s    ?? 0).toFixed(2)  + " m/s"],
      ...beta1.surfaces.map((s) => ["β₁ " + s.surface, s.beta1_deg.toFixed(2) + "°"]),
    ]));
  }

  // ── Construction line input tables ──────────────────────────────────────
  const inputTables = constructionLineTables(blade, views);
  if (inputTables.length) {
    children.push(pb());
    children.push(...inputTables);
  }

  // ── Chart pages (order mirrors the 2D View page layout) ─────────────────

  // Hub – Shroud (meridional contour with processed lines)
  if ((views.hub_shroud?.length || views.processed_lines?.length)) {
    children.push(pb(), sectionHeading("Hub – Shroud"));
    children.push(chartImage(await toImg(mecoFigure(views), 900, 660), 590, 433));
  }

  // Conformal Mapping
  if (views.conf_map?.length) {
    children.push(pb(), sectionHeading("Conformal Mapping"));
    children.push(chartImage(await toImg(confFigure(views), 900, 660), 590, 433));
  }

  // Wrap (Top View) — replaces the old θ-vs-r wrapping diagram
  if (views.wrap?.length) {
    children.push(pb(), sectionHeading("Wrap (Top View)"));
    children.push(chartImage(await toImg(wrapTopFigure(views), 900, 660), 590, 433));
  }

  // Meridional Cone Area Development
  const coneAreaFig = coneAreaFigure(views);
  if (coneAreaFig) {
    children.push(pb(), sectionHeading("Meridional Cone Area Development"));
    children.push(chartImage(await toImg(coneAreaFig, 900, 600), 590, 393));
  }

  // Cone Pairs
  const conePairsFig = conePairsFigure(views);
  if (conePairsFig) {
    children.push(pb(), sectionHeading("Cone Pairs"));
    children.push(chartImage(await toImg(conePairsFig, 900, 660), 590, 433));
  }

  // Curvature (Porcupine)
  const curvFig = curvatureFigure(views);
  if (curvFig) {
    children.push(pb(), sectionHeading("Curvature (Porcupine)"));
    children.push(chartImage(await toImg(curvFig, 900, 660), 590, 433));
    children.push(captionPara("Signed curvature κ = r′z″ − z′r″, normalised to ±1, plotted as normal vectors (scale = 30 mm)"));
  }

  // Beta Angles
  const hasBeta = (blade.construction_lines ?? []).some(
    (cl) => cl.active && cl.beta_spline?.control_points?.length
  );
  if (hasBeta) {
    children.push(pb(), sectionHeading("Blade Angle Distribution"));
    children.push(chartImage(await toImg(betaFigure(blade.construction_lines)), 590, 406));
  }

  // Thickness
  const hasThick = (blade.construction_lines ?? []).some(
    (cl) => cl.active && cl.thickness_spline?.control_points?.length
  );
  if (hasThick) {
    children.push(pb(), sectionHeading("Blade Thickness Distribution"));
    children.push(chartImage(await toImg(thicknessFigure(blade.construction_lines)), 590, 406));
  }

  // 3D Blade View (full impeller with hub/shroud revolution meshes)
  const fig3d = view3dFigure(views, blade.n_blades);
  if (fig3d) {
    children.push(pb(), sectionHeading("3D Blade View"));
    children.push(chartImage(await toImg(fig3d, 960, 720), 590, 443));
  }

  // Spanwise curvature — parameter space
  const curvParamFig = curvParamFigure(curvData);
  if (curvParamFig) {
    children.push(pb(), sectionHeading("Spanwise Curvature κᵥ  —  Parameter Space"));
    children.push(chartImage(await toImg(curvParamFig, 960, 720), 590, 443));
  }

  // Skeleton surface — physical space
  const curvSkelFig = curvSkeletonFigure(curvData);
  if (curvSkelFig) {
    children.push(pb(), sectionHeading("Skeleton Surface  —  Physical Space"));
    children.push(chartImage(await toImg(curvSkelFig, 960, 720), 590, 443));
  }

  // ── Assemble & download ─────────────────────────────────────────────────

  const doc = new Document({
    creator: "FastBlade",
    title:   "Blade Design Report — " + blade.name,
    sections: [{ children }],
  });

  const blob = await Packer.toBlob(doc);
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = blade.name + "_report.docx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
